import { useEffect, useRef } from 'react';
import { resolveSlideKeyNavigation } from '@/lib/quoteCarousel/slideKeyNavigation';

/** Controls that own the arrow/Home/End keys themselves. */
const KEY_OWNING_ROLES = new Set([
  'slider', 'spinbutton', 'combobox', 'listbox', 'option', 'menu', 'menuitem', 'menuitemradio',
  'menuitemcheckbox', 'tab', 'tablist', 'textbox', 'radio', 'radiogroup', 'grid', 'tree', 'treeitem',
]);

/** Whether a keydown target should keep its own arrow/Home/End handling. */
export function isKeyOwningTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.closest('[contenteditable="true"], [contenteditable=""]')) return true;
  if (target.closest('input, textarea, select')) return true;
  let node: HTMLElement | null = target;
  while (node) {
    const role = node.getAttribute('role');
    if (role && KEY_OWNING_ROLES.has(role)) return true;
    node = node.parentElement;
  }
  return false;
}

/** Any modal/non-modal dialog (Radix Dialog/AlertDialog) currently open. */
export function isDialogOpen(): boolean {
  return !!document.querySelector('[role="dialog"], [role="alertdialog"]');
}

/**
 * Page-level slide navigation: ←/→ previous/next, Home/End first/last.
 * Ignored while typing (inputs, textareas, selects, contenteditable, sliders
 * and other key-owning widgets), while a dialog is open, with a modifier
 * held, or when another handler already consumed the key.
 */
export function useSlideKeyboardNavigation({
  slideIds, selectedId, onSelect, enabled = true,
}: {
  slideIds: readonly string[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  enabled?: boolean;
}) {
  const stateRef = useRef({ slideIds, selectedId, onSelect });
  stateRef.current = { slideIds, selectedId, onSelect };

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      if (isKeyOwningTarget(event.target) || isDialogOpen()) return;
      const { slideIds: ids, selectedId: current, onSelect: select } = stateRef.current;
      const next = resolveSlideKeyNavigation({
        key: event.key,
        currentIndex: current ? ids.indexOf(current) : -1,
        count: ids.length,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
      });
      if (next === null) return;
      event.preventDefault();
      select(ids[next]);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
